import React, { useState } from 'react';
import { UserPlus, Shield, ArrowLeft, Sparkles, Mail, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { validateRegisterForm, getPasswordStrength } from '../utils/validation';
import { sendVerificationCode, verifyCode } from '../utils/emailVerification';
interface PatientSignupPageProps {
  onBackToLogin: () => void;
}
export function PatientSignupPage({
  onBackToLogin
}: PatientSignupPageProps) {
  const {
    register
  } = useAuth();
  const [step, setStep] = useState<'form' | 'verify'>('form');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const passwordStrength = password ? getPasswordStrength(password) : null;
  const strengthColors = {
    weak: 'bg-red-400',
    medium: 'bg-amber-400',
    strong: 'bg-emerald-400',
    very_strong: 'bg-emerald-500'
  };
  const handleSendCode = async () => {
    setRegisterError('');
    setVerificationError('');
    const validation = validateRegisterForm(name, email, password, confirmPassword);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }
    setErrors({});
    setIsSendingCode(true);
    const result = await sendVerificationCode(email);
    if (result.success) {
      setStep('verify');
    } else {
      setRegisterError(result.error || 'Error al enviar código de verificación');
    }
    setIsSendingCode(false);
  };
  const handleVerifyCode = async () => {
    setVerificationError('');
    if (!verificationCode || verificationCode.length !== 6) {
      setVerificationError('Por favor ingresa el código de 6 dígitos');
      return;
    }
    setIsVerifying(true);
    const result = await verifyCode(email, verificationCode);
    if (result.success) {
      await handleRegister();
    } else {
      setVerificationError(result.error || 'Código incorrecto');
    }
    setIsVerifying(false);
  };
  const handleRegister = async () => {
    setIsLoading(true);
    const result = await register(name, email, password, 'paciente');
    if (!result.success) {
      setRegisterError(result.error || 'Error al crear la cuenta');
      setStep('form');
    }
    setIsLoading(false);
  };
  const handleResendCode = async () => {
    setVerificationError('');
    setIsSendingCode(true);
    const result = await sendVerificationCode(email);
    if (result.success) {
      setVerificationError('');
      setVerificationCode('');
    } else {
      setVerificationError(result.error || 'Error al reenviar código');
    }
    setIsSendingCode(false);
  };
  // Verification Step
  if (step === 'verify') {
    return <div className="min-h-screen bg-gradient-to-br from-cyan-50/50 via-white to-teal-50/30 flex items-center justify-center p-4" data-id="element-5768">
        <div className="w-full max-w-md" data-id="element-5769">
          <button onClick={() => setStep('form')} className="mb-8 flex items-center gap-2 text-gray-500 hover:text-teal-600 transition-colors group" data-id="element-5770">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" data-id="element-5771" />
            <span className="text-sm font-medium" data-id="element-5772">Volver</span>
          </button>

          <div className="bg-white rounded-3xl shadow-lg shadow-teal-100/50 p-8 sm:p-10 border border-gray-100" data-id="element-5773">
            <div className="text-center mb-8" data-id="element-5774">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl shadow-lg shadow-teal-200/50 mb-6" data-id="element-5775">
                <Mail className="w-8 h-8 text-white" strokeWidth={2.5} data-id="element-5776" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5777">
                Verifica tu correo
              </h2>
              <p className="text-sm text-gray-600 mb-1" data-id="element-5778">Código enviado a</p>
              <p className="text-sm font-semibold text-teal-600" data-id="element-5779">{email}</p>
            </div>

            {verificationError && <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl" data-id="element-5780">
                <p className="text-sm text-red-700 font-medium" data-id="element-5781">
                  {verificationError}
                </p>
              </div>}

            <div className="space-y-6" data-id="element-5782">
              <div data-id="element-5783">
                <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-5784">
                  Código de Verificación
                </label>
                <input type="text" maxLength={6} value={verificationCode} onChange={e => setVerificationCode(e.target.value.replace(/\D/g, ''))} className="w-full px-4 py-4 text-center text-2xl font-bold tracking-[0.5em] border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-teal-100 focus:border-teal-500 transition-all outline-none" placeholder="000000" autoFocus data-id="element-5785" />
                <p className="text-xs text-gray-500 mt-2 text-center" data-id="element-5786">
                  El código expira en 10 minutos
                </p>
              </div>

              <button onClick={handleVerifyCode} disabled={isVerifying || verificationCode.length !== 6} className="w-full h-12 text-base font-semibold bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white shadow-lg shadow-teal-200/50 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5787">
                {isLoading ? 'Creando cuenta...' : isVerifying ? 'Verificando...' : 'Verificar y Crear Cuenta'}
              </button>

              <div className="text-center pt-2" data-id="element-5788">
                <p className="text-sm text-gray-600 mb-2" data-id="element-5789">
                  ¿No recibiste el código?
                </p>
                <button onClick={handleResendCode} disabled={isSendingCode} className="text-sm text-teal-600 font-semibold hover:text-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-5790">
                  {isSendingCode ? 'Reenviando...' : 'Reenviar código'}
                </button>
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-center gap-2 text-xs text-gray-500" data-id="element-5791">
            <Shield className="w-4 h-4 text-teal-600" data-id="element-5792" />
            <span data-id="element-5793">Verificación segura de correo electrónico</span>
          </div>
        </div>
      </div>;
  }
  // Registration Form Step
  return <div className="min-h-screen bg-gradient-to-br from-cyan-50/50 via-white to-teal-50/30 flex items-center justify-center p-4" data-id="element-5794">
      <div className="w-full max-w-md" data-id="element-5795">
        <button onClick={onBackToLogin} className="mb-8 flex items-center gap-2 text-gray-500 hover:text-teal-600 transition-colors group" data-id="element-5796">
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" data-id="element-5797" />
          <span className="text-sm font-medium" data-id="element-5798">
            Volver al inicio de sesión
          </span>
        </button>

        <div className="text-center mb-8" data-id="element-5799">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl shadow-lg shadow-teal-200/50 mb-6" data-id="element-5800">
            <UserPlus className="w-8 h-8 text-white" strokeWidth={2.5} data-id="element-5801" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-5802">
            Crear Cuenta de Paciente
          </h1>
          <div className="flex items-center justify-center gap-2 text-teal-600" data-id="element-5803">
            
            <p className="text-sm font-medium" data-id="element-5804">Sistema Savidna</p>
            
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-lg shadow-teal-100/50 p-8 sm:p-10 border border-gray-100" data-id="element-5805">
          <div className="mb-8" data-id="element-5806">
            <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-5807">Bienvenido</h2>
            <p className="text-sm text-gray-600" data-id="element-5808">
              Crea tu cuenta para registrar tu información médica de forma
              segura
            </p>
          </div>

          {registerError && <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl" data-id="element-5809">
              <p className="text-sm text-red-700 font-medium" data-id="element-5810">
                {registerError}
              </p>
            </div>}

          <form onSubmit={e => {
          e.preventDefault();
          handleSendCode();
        }} className="space-y-5" data-id="element-5811">
            <div data-id="element-5812">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-5813">
                Nombre Completo
              </label>
              <input type="text" placeholder="Juan Pérez" value={name} onChange={e => setName(e.target.value)} className={`w-full px-4 py-3 border-2 rounded-xl transition-all outline-none focus:ring-4 focus:ring-teal-100 focus:border-teal-500 ${errors.name ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-5814" />
              {errors.name && <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1" data-id="element-5815">
                  {errors.name}
                </p>}
            </div>

            <div data-id="element-5816">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-5817">
                Correo Electrónico
              </label>
              <input type="email" placeholder="correo@ejemplo.com" value={email} onChange={e => setEmail(e.target.value)} className={`w-full px-4 py-3 border-2 rounded-xl transition-all outline-none focus:ring-4 focus:ring-teal-100 focus:border-teal-500 ${errors.email ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-5818" />
              {errors.email && <p className="text-xs text-red-600 mt-1.5" data-id="element-5819">{errors.email}</p>}
            </div>

            <div data-id="element-5820">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-5821">
                Contraseña
              </label>
              <div className="relative" data-id="element-5822">
                <input type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} className={`w-full px-4 py-3 pr-11 border-2 rounded-xl transition-all outline-none focus:ring-4 focus:ring-teal-100 focus:border-teal-500 ${errors.password ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-5823" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1" data-id="element-5824">
                  {showPassword ? <EyeOff className="w-5 h-5" data-id="element-5825" /> : <Eye className="w-5 h-5" data-id="element-5826" />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-600 mt-1.5" data-id="element-5827">{errors.password}</p>}
              {password && passwordStrength && <div className="mt-3" data-id="element-5828">
                  <div className="flex items-center gap-2 mb-1" data-id="element-5829">
                    <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-5830">
                      <div className={`h-full rounded-full transition-all duration-500 ${strengthColors[passwordStrength.level]}`} style={{
                    width: `${Math.min(100, passwordStrength.score / 7 * 100)}%`
                  }} data-id="element-5831" />
                    </div>
                    <span className={`text-xs font-semibold ${passwordStrength.level === 'weak' ? 'text-red-500' : passwordStrength.level === 'medium' ? 'text-amber-500' : 'text-emerald-500'}`} data-id="element-5832">
                      {passwordStrength.label}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-5833">
                    Mín. 8 caracteres, mayúscula, minúscula, número y símbolo
                  </p>
                </div>}
            </div>

            <div data-id="element-5834">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-5835">
                Confirmar Contraseña
              </label>
              <div className="relative" data-id="element-5836">
                <input type={showConfirmPassword ? 'text' : 'password'} placeholder="••••••••" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className={`w-full px-4 py-3 pr-11 border-2 rounded-xl transition-all outline-none focus:ring-4 focus:ring-teal-100 focus:border-teal-500 ${errors.confirmPassword ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-5837" />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1" data-id="element-5838">
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" data-id="element-5839" /> : <Eye className="w-5 h-5" data-id="element-5840" />}
                </button>
              </div>
              {errors.confirmPassword && <p className="text-xs text-red-600 mt-1.5" data-id="element-5841">
                  {errors.confirmPassword}
                </p>}
            </div>

            <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-teal-50/80 to-cyan-50/50 border border-teal-100 rounded-2xl" data-id="element-5842">
              <Shield className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" data-id="element-5843" />
              <div data-id="element-5844">
                <p className="text-xs text-teal-900 font-semibold mb-0.5" data-id="element-5845">
                  Información Segura y Confidencial
                </p>
                <p className="text-xs text-teal-700 leading-relaxed" data-id="element-5846">
                  Tu contraseña se almacena de forma segura con cifrado SHA-256.
                  Toda tu información médica está protegida.
                </p>
              </div>
            </div>

            <button type="submit" disabled={isSendingCode} className="w-full h-12 text-base font-semibold bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white shadow-lg shadow-teal-200/50 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5847">
              {isSendingCode ? 'Enviando código...' : 'Continuar'}
            </button>
          </form>

          <div className="mt-6 text-center" data-id="element-5848">
            <p className="text-sm text-gray-600" data-id="element-5849">
              ¿Ya tienes una cuenta?{' '}
              <button onClick={onBackToLogin} className="text-teal-600 font-semibold hover:text-teal-700 transition-colors" data-id="element-5850">
                Iniciar sesión
              </button>
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-gray-500 mt-6 leading-relaxed" data-id="element-5851">
          Al crear una cuenta, recibirás un código de verificación en tu correo
          para confirmar tu identidad.
        </p>
      </div>
    </div>;
}